import React, { useEffect, useState, useRef } from "react";
import * as echarts from "echarts";
import axios from "axios";
import "./geomap.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import CRCNutslever from "../../utils/crcNutsLevel.json";
import { useDatasets } from "../../hooks/useDatasets";

// Region field by NUTS level - handles different column naming conventions per dataset
const getRegionNameKey = (level, datasetLabel, category) => {
  // Risk Scores datasets use 'regionCode' to match with GeoJSON NUTS_ID
  if (category === "Risk Scores") {
    return "regionCode";
  }
  // Synthetic CRC dataset uses different column names
  if (datasetLabel === "Synthetic CRC") {
    switch (level) {
      case "NUTS1":
        return "NUTS-1";
      case "NUTS2":
        return "NUTS-2";
      case "NUTS3":
        return "NUTS-3_Short";
      default:
        return "NUTS-1";
    }
  }
  // Default column names for other datasets (e.g., Factors Related to Screening)
  switch (level) {
    case "NUTS1":
      return "Region";
    case "NUTS2":
      return "Province";
    case "NUTS3":
      return "Arrondissement";
    default:
      return "Region";
  }
};

// Check if dataset is a Risk Scores dataset
const isRiskScoresDataset = (label) => {
  return label && label.startsWith("Risk assessments for the synthetic dataset");
};

// Heuristic: variables that look like proportions/percents (0..1)
const isPercentLike = (name) =>
  /coverage|response\s*rate|rate\b|^prop\b|percentage/i.test(name);

const GeoMap = ({ chartName, label, category }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null); // persistent ECharts instance

  const [nutsLevel, setNutsLevel] = useState("NUTS3");
  const [mapName, setMapName] = useState(""); // registered map name

  // Set default NUTS level based on category (Risk Scores defaults to NUTS1)
  useEffect(() => {
    if (category === "Risk Scores") {
      setNutsLevel("NUTS1");
    }
  }, [category]);
  const [dataUseInMap, setDataUseInMap] = useState([]);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [selectedData, setSelectedData] = useState([]);

  const { getSpecificDataset } = useDatasets();

  const urlMap = {
    NUTS1: `${process.env.PUBLIC_URL}/NUTS_RG_20M_2021_3035_N1_v2.geojson`,
    NUTS2: `${process.env.PUBLIC_URL}/NUTS_RG_20M_2021_3035_N2_v2.geojson`,
    NUTS3: `${process.env.PUBLIC_URL}/NUTS_RG_20M_2021_3035_N3_v2.geojson`,
  };

  // Init ECharts once
  useEffect(() => {
    if (chartRef.current || !containerRef.current) return;
    const chart = echarts.init(containerRef.current);
    chartRef.current = chart;

    const onClick = (params) => {
      if (params?.data?.items) setSelectedData(params.data.items);
    };
    chart.on("click", onClick);

    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);

    return () => {
      chart.off("click", onClick);
      window.removeEventListener("resize", onResize);
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  // Load dataset via CRCNutslever[label][nutsLevel] with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (!label) {
        setDataUseInMap([]);
        setAvailableMetrics([]);
        setSelectedMetric(null);
        return;
      }

      const entry = CRCNutslever[label];
      if (!entry) {
        console.warn(`No entry found for label: ${label}`);
        setDataUseInMap([]);
        setAvailableMetrics([]);
        setSelectedMetric(null);
        return;
      }

      const datasetId = entry[nutsLevel];
      if (!datasetId) {
        console.warn(`No dataset ID found for ${label} at ${nutsLevel}`);
        setDataUseInMap([]);
        setAvailableMetrics([]);
        setSelectedMetric(null);
        return;
      }

      try {
        // Clear current data while loading to prevent conflicts
        setDataUseInMap([]);
        setSelectedData([]);

        const ds = await getSpecificDataset(datasetId);
        if (ds && ds.elements) {
          setDataUseInMap(ds.elements);
        } else {
          setDataUseInMap([]);
        }
      } catch (e) {
        console.error("Dataset fetch failed:", e);
        setDataUseInMap([]);
        setAvailableMetrics([]);
        setSelectedMetric(null);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [label, nutsLevel, getSpecificDataset]);

  // Build numeric metric list
  useEffect(() => {
    if (!dataUseInMap.length) {
      setAvailableMetrics([]);
      setSelectedMetric(null);
      return;
    }
    const regionKey = getRegionNameKey(nutsLevel, label, category);

    // Define columns to exclude based on dataset type
    let additionalDeny = [];
    if (label === "Synthetic CRC") {
      additionalDeny = ["NUTS-1", "NUTS-2", "NUTS-3_Long", "NUTS-3_Short", "Code"];
    } else if (category === "Risk Scores") {
      // Exclude non-numeric and metadata columns for Risk Scores
      additionalDeny = [
        "regionLevel", "regionCode", "regionName",
        "NUTS1", "NUTS1_name", "NUTS2", "NUTS2_name", "NUTS3", "NUTS3_name",
        "sexCounts", "sexProportions", "riskLabelCounts", "riskLabelProportions",
        "observationNumericMeans", "observationCategoricalDistributions"
      ];
    }
    const deny = new Set([regionKey, "Year", ...additionalDeny]);

    const keys = Object.keys(
      dataUseInMap.reduce((acc, r) => {
        Object.keys(r).forEach((k) => (acc[k] = true));
        return acc;
      }, {})
    );

    const numeric = keys
      .filter((k) => !deny.has(k))
      .filter((k) =>
        dataUseInMap.some((r) => Number.isFinite(parseFloat(r[k])))
      );

    // Prioritize metrics based on category
    const prioritize = category === "Risk Scores"
      ? ["avgRiskScore", "avgAge", "avgBMI", "numPatients", "avgHeight_cm", "avgWeight_kg"]
      : ["Screening Coverage", "Screening Response Rate", "Population Size", "Patients per GP Practice"];

    numeric.sort((a, b) => {
      const ia = prioritize.indexOf(a);
      const ib = prioritize.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });

    setAvailableMetrics(numeric);
    setSelectedMetric((prev) => prev || numeric[0] || null);
  }, [dataUseInMap, nutsLevel, label, category]);

  // Load GeoJSON & register map (unique name per level) with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      try {
        // Clear current map while loading new one
        setMapName("");

        const resp = await axios.get(urlMap[nutsLevel]);
        const geoJson = resp.data;

        if (!geoJson || !geoJson.features) {
          console.error("Invalid GeoJSON data received");
          return;
        }

        // For Risk Scores, use NUTS_ID as the name property to match with regionCode
        // For other datasets, use NUTS_NAME
        geoJson.features.forEach((f) => {
          if (category === "Risk Scores") {
            f.properties.name = f.properties.NUTS_ID?.trim();
          } else {
            f.properties.name = f.properties.NUTS_NAME?.trim();
          }
        });

        // Include category in map name to ensure different maps for different categories
        const name = `dynamicGeoMap_${nutsLevel}_${category || 'default'}`;

        // Unregister previous map if exists
        try {
          echarts.getMap(name) && echarts.registerMap(name, null);
        } catch (e) {
          // Map doesn't exist, continue
        }

        echarts.registerMap(name, geoJson);
        setMapName(name);
      } catch (e) {
        console.error("GeoJSON fetch failed:", e);
        setMapName("");
      }
    }, 200); // 200ms debounce for GeoJSON loading

    return () => clearTimeout(timeoutId);
  }, [nutsLevel, category]);

  // Update chart option (full rebuild, no partial merges)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !mapName || !selectedMetric || !dataUseInMap.length) {
      // Clear chart if conditions not met
      if (chart) {
        chart.clear();
      }
      return;
    }

    const regionKey = getRegionNameKey(nutsLevel, label, category);

    // Group rows by region
    const grouped = dataUseInMap.reduce((acc, item) => {
      const key = (item[regionKey] || "").trim();
      if (!key) return acc;
      (acc[key] ||= []).push(item);
      return acc;
    }, {});

    // Compute averages for the selected metric
    const rawSeries = Object.keys(grouped).map((name) => {
      const vals = grouped[name]
        .map((d) => parseFloat(d[selectedMetric]))
        .filter((v) => Number.isFinite(v));
      const avg = vals.length
        ? vals.reduce((s, v) => s + v, 0) / vals.length
        : NaN;
      return {
        name,
        value: Number.isFinite(avg) ? avg : undefined, // <-- no nulls/NaN
        items: grouped[name],
      };
    });

    // Keep only entries that have a finite value to avoid ECharts’ rawData glitches
    const seriesData = rawSeries.filter((s) => Number.isFinite(s.value));

    const values = seriesData.map((s) => s.value);
    let minVal = values.length ? Math.min(...values) : 0;
    let maxVal = values.length ? Math.max(...values) : 1;

    const pct = isPercentLike(selectedMetric);
    if (pct) {
      minVal = Math.max(0, minVal);
      maxVal = Math.min(1, Math.max(1, maxVal));
    } else if (minVal === maxVal) {
      minVal = minVal * 0.9;
      maxVal = maxVal * 1.1 || 1;
    }

    const option = {
      animation: false,
      tooltip: {
        trigger: "item",
        confine: true,
        enterable: false,
        hideDelay: 0,
        showDelay: 0,
        transitionDuration: 0,
        backgroundColor: "rgba(0,0,0,0.8)",
        borderColor: "transparent",
        textStyle: {
          color: "#fff",
        },
        formatter: (p) => {
          const v = p?.value;
          const display =
            v == null || !isFinite(v)
              ? "N/A"
              : pct
              ? `${(v * 100).toFixed(1)}%`
              : Number.isInteger(v) ? v.toLocaleString() : v.toFixed(2);
          // For Risk Scores, show regionName from the data items instead of regionCode
          const displayName = category === "Risk Scores" && p?.data?.items?.[0]?.regionName
            ? p.data.items[0].regionName
            : p?.name ?? "";
          return `${displayName}<br/>${selectedMetric}: ${display}`;
        },
      },
      visualMap: {
        left: "right",
        min: minVal,
        max: maxVal,
        inRange: {
          color: [
            "#1a9850",
            "#66bd63",
            "#a6d96a",
            "#d9ef8b",
            "#ffffbf",
            "#fee08b",
            "#fdae61",
            "#f46d43",
            "#d73027",
          ],
        },
        text: ["High", "Low"],
        calculable: true,
      },

      // >>> THIS is the key part <<<
      // Different center/zoom for Greece (Risk Scores) vs EU (other datasets)
      geo: {
        map: mapName,
        nameProperty: "name",
        roam: true, // enable pan/zoom
        // Set center and zoom based on category
        // Greece coordinates in EPSG:3035: approximately [5270000, 1950000]
        // EU center in EPSG:3035: [4025883.6818, 3075418.9511]
        center: category === "Risk Scores"
          ? [5270000, 1950000] // Greece center in EPSG:3035
          : [4025883.6818, 3075418.9511], // EU center
        zoom: category === "Risk Scores"
          ? 20 // Zoom for Greece (lower for wider view)
          : 40, // Default zoom for EU
        scaleLimit: { min: 0.1, max: 150 }, // allow zoom out and deep zoom
        // Let ECharts size it to the container (keeps zoom above)
        layoutCenter: ["50%", "50%"],
        layoutSize: "100%",
        silent: false,
        itemStyle: {
          areaColor: "#f0f0f0",
          borderColor: "#999",
          borderWidth: 0.5,
        },
        emphasis: {
          itemStyle: {
            areaColor: "#e0e0e0",
            borderColor: "#666",
            borderWidth: 1,
          },
          label: {
            show: false,
          },
        },
      },
      series: [
        {
          type: "map",
          name: "Regions",
          geoIndex: 0, // bind to the geo above
          emphasis: {
            focus: "self",
            itemStyle: {
              areaColor: "#ffa500",
              borderColor: "#333",
              borderWidth: 2,
            },
          },
          select: {
            itemStyle: {
              areaColor: "#ff6b35",
            },
          },
          data: seriesData,
        },
      ],
    };
    // Clear and set new option to prevent state conflicts
    chart.clear();
    chart.setOption(option, true);

    // Add debounced resize to prevent flickering
    const resizeTimeout = setTimeout(() => {
      if (chart && !chart.isDisposed()) {
        chart.resize();
      }
    }, 100);

    return () => clearTimeout(resizeTimeout);
  }, [mapName, selectedMetric, dataUseInMap, nutsLevel, label, category]);

  return (
    <>
      {/* Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label htmlFor="metric">Metric:</label>
          <select
            id="metric"
            value={selectedMetric || ""}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="form-select"
            style={{ width: 300 }}
            disabled={!availableMetrics.length}
          >
            {availableMetrics.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label htmlFor="nutsLevel">NUTS Level:</label>
          <select
            id="nutsLevel"
            value={nutsLevel}
            onChange={(e) => setNutsLevel(e.target.value)}
            className="form-select"
            style={{ width: 260 }}
          >
            <option value="NUTS1">NUTS1 - Large Regions</option>
            <option value="NUTS2">NUTS2 - Medium Regions</option>
            <option value="NUTS3">NUTS3 - Small Regions</option>
          </select>
        </div>
      </div>

      {/* Chart */}
      <div
        id={chartName}
        ref={containerRef}
        style={{
          width: "100%",
          height: "600px",
          minWidth: 300,
          touchAction: "none",
        }}
      />

      {/* Table on click */}
      {selectedData.length > 0 && (
        <div className="mt-4">
          <h5>Selected Region Data</h5>
          <table className="table table-bordered table-striped">
            <thead>
              <tr>
                {Object.keys(selectedData[0]).map((k) => (
                  <th key={k}>{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedData.map((row, i) => (
                <tr key={i}>
                  {Object.values(row).map((v, j) => (
                    <td key={j}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default GeoMap;
